import { useState, useEffect } from "react";
import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import SettingsCard from "components/commons/SettingsCard";
import SuccessMessage from "components/commons/SuccessMessage";
import { useCountHealthDataQuery, useDeleteHealthDataMutation, useGetHealthSettingsQuery, useSetHealthSettingsMutation } from "./api/healthSettingsApiSlice";
import { Dialog } from "primereact/dialog";
import Loading from "components/commons/Loading";

export default function HealthSettings() {
    const [t] = useTranslation(["global", "health"]);

    const [settings, setSettings] = useState();
    const [statusMessage, setStatusMessage] = useState("");

    const {
        data,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch
    } = useGetHealthSettingsQuery();

    const [saveSettings, {
        isLoading: saveIsLoading,
        isSuccess: saveIsSuccess,
        isError: saveIsError,
        error: saveError,
        reset: resetSaveStatus
    }] = useSetHealthSettingsMutation();

    useEffect(() => {
        if (data && isSuccess) {
            setSettings(data);
        }
    }, [data, isSuccess]);

    const handleChange = (key, value) => {
        setSettings((prevSettings) => ({
            ...prevSettings,
            [key]: value,
        }));
        resetSaveStatus();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        saveSettings(settings);
    };

    const content = (
        <div>
            {isError ? (
                <p>{t("Failed to load settings.")} {error?.data?.message || error?.status}</p>
            ) : isSuccess && settings ? (
                <form >
                    {saveIsSuccess && <SuccessMessage />}
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mb-4">
                        <div className="col">
                            <SettingsCard
                                label={t("health:setting.measure_completed_notifications.title")}
                                description={t("health:setting.measure_completed_notifications.desc")}
                                logo={<i className="fa-solid fa-flask"></i>}
                                value={settings.measure_completed_notifications}
                                measureUnit={"mins"}
                                onChange={(e) => handleChange("measure_completed_notifications", e.target.checked)}
                                inputType="checkbox"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("health:setting.threshold_exceed_notifications.title")}
                                description={t("health:setting.threshold_exceed_notifications.desc")}
                                logo={<i className="fa-solid fa-heart-circle-exclamation"></i>}
                                value={settings.threshold_exceed_notifications}
                                measureUnit={"mins"}
                                onChange={(e) => handleChange("threshold_exceed_notifications", e.target.checked)}
                                inputType="checkbox"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("health:setting.health_data_sync_period.title")}
                                description={t("health:setting.health_data_sync_period.desc")}
                                logo={<i className="fa-solid fa-cloud-arrow-up"></i>}
                                value={settings.sync_period}
                                measureUnit={t("unit.minutes")}
                                onChange={(e) => handleChange("sync_period", parseInt(e.target.value, 10))}
                                inputType="number"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("health:setting.emergency_phone_number.title")}
                                description={t("health:setting.emergency_phone_number.desc")}
                                logo={<i className="fa-solid fa-phone-volume"></i>}
                                value={settings.default_emergency_number}
                                onChange={(e) => handleChange("default_emergency_number", e.target.value)}
                                inputType="text"
                            />
                        </div>
                        <div className="col">
                            <div className="card bg-dark text-white h-100 d-flex flex-column">
                                <div className="card-body d-flex flex-column">
                                    <div className="row align-items-center">
                                        <div className="col-auto text-muted">
                                            <h2 className="mb-0"><i className="fa-solid fa-eraser"></i></h2>
                                        </div>
                                        <div className="col">
                                            <h5 className="card-title">{t("health:setting.data_reset.title")}</h5>
                                        </div>
                                    </div>

                                    <p className="card-text text-muted flex-grow-1 mt-2">{t("health:setting.data_reset.desc")}</p>

                                    <div className=" align-items-center mt-auto">
                                        <DeleteHealthDataDialog />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button type="submit" onClick={handleSubmit} className="btn btn-primary"><i className="fa-solid fa-floppy-disk"></i> {t("action.save")}</button>


                </form>


            ) : <></>
            }
        </div >
    );

    return (
        <PageLayout
            pageTitle={t("Health Settings")}
            bodyContent={content}
            onPageRefresh={refetch}
            isLoadingContent={saveIsLoading || isFetching}
            isLoadingPage={isLoading}
            isError={saveIsError || isError}
            errorMsg={saveError || error}
        />
    );
}

const DeleteHealthDataDialog = () => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        resetDeleteStatus()
    }, [openDialog])
    const {
        data,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch
    } = useCountHealthDataQuery();

    const [deleteData, {
        isLoading: deleteIsLoading,
        isSuccess: deleteIsSuccess,
        isError: deleteIsError,
        error: deleteError,
        reset: resetDeleteStatus
    }] = useDeleteHealthDataMutation();


    return (
        <>
            <button className="btn btn-danger" onSubmit={null} onClick={() => setOpenDialog(true)}>{t("health:setting.data_reset.button").toUpperCase()}</button>
            <Dialog header={t("health:setting.data_reset.title")} visible={openDialog} onHide={() => setOpenDialog(false)} >
                {
                    deleteIsSuccess &&
                    <SuccessMessage />

                }
                {
                    (isFetching || deleteIsLoading) && <Loading />
                }

                {
                    isSuccess && !isFetching &&
                    // <p>Cancellare <b>{data.total}</b> dati health ?</p>

                    <p>{t("health:setting.data_reset.confirm_question", { count: data.total })}</p>


                }
                <button className="btn btn-outline-secondary me-2" onClick={refetch}><i className="fa-solid fa-arrows-rotate"></i> {t("action.refresh")}</button>

                <button className="btn btn-danger" onClick={deleteData}>{t("health:setting.data_reset.button").toUpperCase()}</button>

            </Dialog>
        </>
    );
};
